/**
 * Onboarding form logic, kept out of the component so it can be tested without React.
 *
 * Note what is NOT here: any copy of the upload rules. The server's messages are already written
 * for the applicant ("That file is larger than 15MB…"), and S3 enforces the limits itself, so
 * mirroring the allowlist here would only create something to drift. The presign call sends
 * metadata rather than the file, so the round trip that produces the error is cheap.
 */

/**
 * The application as the server reports it. Mirrors ApplicationStatusView.
 *
 * Redeclared rather than imported: it belongs to the onboarding service, and the console already
 * treats each service's HTTP contract as its own thing rather than reaching into its source.
 */
export interface ApplicationView {
  applicationId: string;
  state: string;
  restaurantName?: string;
  contactEmail: string;
  timezone?: string;
  existingPhoneE164?: string;
  preferredAreaCode?: string;
  allocatedPhoneE164?: string;
  nextStep?: string;
  missing?: string[];
  problem?: string;
}

export interface UploadedFileView {
  fileId: string;
  filename: string;
  sizeBytes: number;
  uploadedAtIso: string;
}

export type OnboardingStep = "form" | "submitted";

/**
 * DRAFT is the only editable state, matching the server. Everything from APPLIED onward is a human
 * acting on the application, so the applicant sees status rather than a form.
 */
export function onboardingStep(view: ApplicationView | undefined): OnboardingStep {
  if (!view) return "form";
  return view.state === "DRAFT" ? "form" : "submitted";
}

/** Human file size for the attachment list. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
